import { useCallback, useEffect, useMemo, useState } from "react";
import {
  BadgeCheck,
  ChevronLeft,
  ChevronUp,
  GraduationCap,
  History,
  Info,
  Layers,
  Lightbulb,
  Lock,
  Sparkles,
  TextCursorInput,
  Trophy,
} from "lucide-react";
import { store } from "../../redux/store";
import { cardsForRegion, isCardAvailable, regionById, RECALL_REGIONS } from "./cards";
import {
  emptyRecallStore,
  loadRecallStore,
  summarizeCard,
  summarizeOverall,
  summarizeRegion,
} from "./progressService";
import RecallPractice from "./RecallPractice";
import { gradientVars, ProgressRing, REGION_ICONS, StatusChip, useRecallT } from "./ui";
import "./anatomyRecall.css";

const STAGE_LABEL = {
  trace: ["stageTrace", "Trace"],
  recall: ["stageRecall", "Recall"],
  master: ["stageMaster", "Master"],
};

function currentUserId() {
  const auth = store.getState()?.auth || {};
  return auth.id || auth.user_id || "guest";
}

export default function AnatomyRecallTab({ dark }) {
  const { t } = useRecallT();
  const userId = currentUserId();
  const [recallStore, setRecallStore] = useState(emptyRecallStore());
  const [view, setView] = useState({ screen: "regions" });
  const [openInfo, setOpenInfo] = useState(null);

  const reload = useCallback(() => {
    loadRecallStore(userId).then(setRecallStore);
  }, [userId]);

  useEffect(() => {
    reload();
  }, [reload, view.screen]);

  const go = (next) => {
    setView(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const rootClass = `ar-root ${dark ? "ar-dark" : ""}`;

  if (view.screen === "practice") {
    return (
      <div className={rootClass}>
        <RecallPractice
          cardId={view.cardId}
          initialStage={view.stage}
          dark={dark}
          userId={userId}
          onExit={() => go({ screen: "cards", regionId: view.regionId })}
        />
      </div>
    );
  }

  if (view.screen === "cards") {
    return (
      <div className={rootClass}>
        <CardsView
          regionId={view.regionId}
          recallStore={recallStore}
          openInfo={openInfo}
          setOpenInfo={setOpenInfo}
          onBack={() => go({ screen: "regions" })}
          onOpen={(cardId, stage) => go({ screen: "practice", regionId: view.regionId, cardId, stage })}
          t={t}
        />
      </div>
    );
  }

  return (
    <div className={rootClass}>
      <RegionsView recallStore={recallStore} onOpen={(regionId) => go({ screen: "cards", regionId })} t={t} />
    </div>
  );
}

function RegionsView({ recallStore, onOpen, t }) {
  const overall = useMemo(() => summarizeOverall(recallStore), [recallStore]);
  const stats = [
    [Layers, `${overall.cardsCompleted}/${overall.cardsAvailable}`, t("statCards", "Cards done")],
    [GraduationCap, overall.structuresLearned, t("statLearned", "Learned")],
    [BadgeCheck, overall.structuresMastered, t("statMastered", "Mastered")],
    [History, overall.structuresDue, t("statReview", "To review")],
  ];
  const steps = [
    [TextCursorInput, t("stageTrace", "Trace"), t("stageTraceBody", "Type over the ghost name")],
    [Lightbulb, t("stageRecall", "Recall"), t("stageRecallBody", "Name it from memory")],
    [Trophy, t("stageMaster", "Master"), t("stageMasterBody", "New angles, no hints")],
  ];

  return (
    <>
      <section className="ar-hero ar-fade">
        <div className="ar-hero-row">
          <div style={{ flex: 1, minWidth: 0 }}>
            <span className="ar-eyebrow">
              <Sparkles size={12} /> {t("eyebrow", "3D MEMORY TRAINER")}
            </span>
            <h2>{t("heroTitle", "See it. Type it. Master it.")}</h2>
            <p>
              {t(
                "heroBody",
                "Complete glass-like 3D models by typing every structure — then recall them without hints.",
              )}
            </p>
          </div>
          <ProgressRing size={96} stroke={8} percent={overall.percent} colors={["#FDE68A", "#FFFFFF"]} track="rgba(255,255,255,0.18)">
            <div>
              <div style={{ fontSize: 22, fontWeight: 900 }}>{overall.percent}%</div>
              <div style={{ fontSize: 10.5, fontWeight: 700, opacity: 0.75 }}>{t("overall", "overall")}</div>
            </div>
          </ProgressRing>
        </div>
        <div className="ar-stats">
          {stats.map(([Icon, value, label]) => (
            <div key={label} className="ar-stat">
              <Icon size={15} style={{ opacity: 0.85 }} />
              <b>{value}</b>
              <span>{label}</span>
            </div>
          ))}
        </div>
      </section>

      <div className="ar-steps">
        {steps.map(([Icon, title, body], i) => (
          <div key={title} className="ar-step ar-fade" style={{ "--i": i + 1 }}>
            <div className="ar-step-head">
              <span className="ar-step-icon">
                <Icon />
              </span>
              <span className="ar-step-index">0{i + 1}</span>
            </div>
            <b>{title}</b>
            <span>{body}</span>
          </div>
        ))}
      </div>

      <div className="ar-section-title">{t("regions", "Choose a region")}</div>
      <div className="ar-regions">
        {RECALL_REGIONS.map((region, i) => {
          const s = summarizeRegion(region.id, recallStore);
          const Icon = REGION_ICONS[region.id];
          return (
            <button
              key={region.id}
              type="button"
              className="ar-region ar-fade"
              style={{ ...gradientVars(region.gradient), "--i": i + 3 }}
              onClick={() => onOpen(region.id)}
            >
              <div className="ar-region-top">
                <span className="ar-region-icon">
                  <Icon />
                </span>
                {s.due > 0 ? (
                  <span className="ar-due">
                    <History /> {s.due}
                  </span>
                ) : null}
              </div>
              <b>{t(`region_${region.id}`, region.title)}</b>
              <small>{t(`regionBlurb_${region.id}`, region.blurb)}</small>
              <div className="ar-region-meta">
                {t("regionMeta", `${s.playable} cards · ${s.structures} structures`, {
                  cards: s.playable,
                  structures: s.structures,
                })}
              </div>
              <div className="ar-bar">
                <i style={{ width: `${Math.max(s.mastery, 2)}%` }} />
              </div>
              <div className="ar-region-pct">
                {s.mastery}% <span>{t("mastery", "mastery")}</span>
              </div>
            </button>
          );
        })}
      </div>
    </>
  );
}

function CardsView({ regionId, recallStore, openInfo, setOpenInfo, onBack, onOpen, t }) {
  const region = regionById(regionId) || RECALL_REGIONS[0];
  const cards = cardsForRegion(region.id);
  const stats = summarizeRegion(region.id, recallStore);
  const Icon = REGION_ICONS[region.id];

  return (
    <div style={gradientVars(region.gradient)}>
      <div className="ar-head ar-fade">
        <button type="button" className="ar-btn-round" onClick={onBack} aria-label={t("back", "Back")}>
          <ChevronLeft />
        </button>
        <span className="ar-head-icon">
          <Icon />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h3>{t(`region_${region.id}`, region.title)}</h3>
          <small>
            {t("regionMeta", `${stats.playable} cards · ${stats.structures} structures`, {
              cards: stats.playable,
              structures: stats.structures,
            })}
          </small>
        </div>
        <ProgressRing size={62} stroke={6} percent={stats.mastery} colors={region.gradient}>
          <b style={{ fontSize: 14 }}>{stats.mastery}%</b>
        </ProgressRing>
      </div>

      <div className="ar-cards">
        {cards.map((card, i) => {
          const available = isCardAvailable(card);
          const summary = summarizeCard(card, recallStore);
          const infoOpen = openInfo === card.id;
          return (
            <button
              key={card.id}
              type="button"
              className={`ar-card ar-fade ${available ? "" : "is-locked"}`}
              style={{ "--i": i }}
              onClick={() =>
                available ? onOpen(card.id, summary.nextStage) : setOpenInfo(infoOpen ? null : card.id)
              }
            >
              <div className="ar-card-main">
                {available ? (
                  <ProgressRing size={56} stroke={5} percent={summary.mastery} colors={region.gradient}>
                    <b style={{ fontSize: 12.5 }}>{summary.mastery}%</b>
                  </ProgressRing>
                ) : (
                  <span className="ar-lock">
                    <Lock size={22} />
                  </span>
                )}
                <div className="ar-card-text">
                  <b>{t(`card_${card.id}`, card.title)}</b>
                  <small>
                    {available
                      ? t("structuresCount", `${summary.total} structures`, { count: summary.total })
                      : t("notInModel", "Not yet supported by the 3D model")}
                  </small>
                  <div className="ar-chips">
                    <StatusChip status={summary.status} comingSoon={!available} t={t} />
                    {available && summary.due > 0 ? (
                      <span className="ar-due">
                        <History /> {t("dueCount", `${summary.due} to review`, { count: summary.due })}
                      </span>
                    ) : null}
                  </div>
                </div>
                {available ? (
                  <div className="ar-stage-col">
                    <div className="ar-stage-dots">
                      {["trace", "recall", "master"].map((stage) => {
                        const done =
                          stage === "trace"
                            ? summary.unlocked.recall
                            : stage === "recall"
                              ? summary.unlocked.master
                              : summary.status === "mastered";
                        const next = summary.nextStage === stage && !done;
                        return <i key={stage} className={done ? "is-done" : next ? "is-next" : ""} />;
                      })}
                    </div>
                    <span className="ar-next">{t(...STAGE_LABEL[summary.nextStage])}</span>
                  </div>
                ) : infoOpen ? (
                  <ChevronUp size={20} color="var(--ar-muted)" />
                ) : (
                  <Info size={20} color="var(--ar-muted)" />
                )}
              </div>
              {!available && infoOpen ? <div className="ar-reason">{card.unavailableReason}</div> : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
